import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { useAtom } from 'jotai'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore';
import { trpc } from '@/lib/trpc';
import {
  Github,
  GitBranch,
  ArrowRight,
  CheckCircle2,
  Circle,
  Settings,
  Plug,
  FileText,
  Users,
  MessageSquare,
  Calendar
} from 'lucide-react';

export const Route = createFileRoute('/(protected)/workspace/integrations/')({
  component: IntegrationsPage,
});

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'connected' | 'available' | 'coming-soon';
  category: 'source' | 'communication' | 'productivity';
  href?: string;
}

function IntegrationsPage() {
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)

  // Check if GitHub App is configured and if user has installations
  const { data: isGitHubConfigured } = trpc.githubApp.isConfigured.useQuery();
  const { data: githubInstallations } = trpc.githubApp.listInstallations.useQuery(
    undefined,
    { enabled: isGitHubConfigured === true }
  );

  // Check if workspace has connected GitHub repositories
  const { data: connectedRepos } = trpc.githubApp.listConnectedRepositories.useQuery(
    { workspaceId: workspace?.id! },
    { enabled: !!workspace?.id && isGitHubConfigured === true }
  );

  // Determine GitHub integration status
  const getGitHubStatus = (): 'connected' | 'available' | 'coming-soon' => {
    if (!isGitHubConfigured) return 'available';
    if (connectedRepos && connectedRepos.length > 0) return 'connected';
    if (githubInstallations && githubInstallations.length > 0) return 'available';
    return 'available';
  };

  const integrations: Integration[] = [
    {
      id: 'github',
      name: 'GitHub',
      description: 'Sync documentation from GitHub repositories',
      icon: <Github className="h-5 w-5" />,
      status: getGitHubStatus(),
      category: 'source',
      href: `/workspace/integrations/github`,
    },
    {
      id: 'gitlab',
      name: 'GitLab',
      description: 'Import and sync GitLab repositories',
      icon: <GitBranch className="h-5 w-5" />,
      status: 'coming-soon',
      category: 'source',
    },
    {
      id: 'notion',
      name: 'Notion',
      description: 'Import pages and databases from Notion',
      icon: <FileText className="h-5 w-5" />,
      status: 'available',
      category: 'source',
    },
    {
      id: 'confluence',
      name: 'Confluence',
      description: 'Import Confluence spaces and pages',
      icon: <FileText className="h-5 w-5" />,
      status: 'coming-soon',
      category: 'source',
    },
    {
      id: 'slack',
      name: 'Slack',
      description: 'Send notifications and updates to Slack',
      icon: <MessageSquare className="h-5 w-5" />,
      status: 'coming-soon',
      category: 'communication',
    },
    {
      id: 'teams',
      name: 'Microsoft Teams',
      description: 'Integrate with Microsoft Teams channels',
      icon: <Users className="h-5 w-5" />,
      status: 'coming-soon',
      category: 'communication',
    },
    {
      id: 'jira',
      name: 'Jira',
      description: 'Link documentation to Jira issues',
      icon: <Calendar className="h-5 w-5" />,
      status: 'coming-soon',
      category: 'productivity',
    },
    {
      id: 'linear',
      name: 'Linear',
      description: 'Connect Linear issues with documentation',
      icon: <Calendar className="h-5 w-5" />,
      status: 'coming-soon',
      category: 'productivity',
    },
  ];

  const categories = [
    { id: 'source', name: 'Source Control & Docs', description: 'Import and sync from various sources' },
    { id: 'communication', name: 'Communication', description: 'Share updates and collaborate' },
    { id: 'productivity', name: 'Productivity', description: 'Connect with project management tools' },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center space-x-2">
          <Plug className="h-8 w-8" />
          <span>Integrations</span>
        </h1>
        <p className="text-muted-foreground mt-2">
          Connect your workspace with external tools and services
        </p>
      </div>

      {/* Categories */}
      {categories.map((category) => {
        const categoryIntegrations = integrations.filter((i) => i.category === category.id);

        return (
          <div key={category.id} className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold">{category.name}</h2>
              <p className="text-sm text-muted-foreground">{category.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryIntegrations.map((integration) => (
                <Card
                  key={integration.id}
                  className={`relative ${
                    integration.status === 'coming-soon' ? 'opacity-75' : ''
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-muted">
                          {integration.icon}
                        </div>
                        <div>
                          <CardTitle className="text-base">{integration.name}</CardTitle>
                          <CardDescription className="text-xs mt-1">
                            {integration.description}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        {integration.status === 'connected' && (
                          <Badge variant="secondary" className="flex items-center space-x-1">
                            <CheckCircle2 className="h-3 w-3" />
                            <span>Connected</span>
                          </Badge>
                        )}
                        {integration.status === 'available' && (
                          <Badge variant="outline" className="flex items-center space-x-1">
                            <Circle className="h-3 w-3" />
                            <span>Available</span>
                          </Badge>
                        )}
                        {integration.status === 'coming-soon' && (
                          <Badge variant="secondary" className="flex items-center space-x-1">
                            <Settings className="h-3 w-3" />
                            <span>Coming Soon</span>
                          </Badge>
                        )}
                      </div>

                      {integration.href && integration.status !== 'coming-soon' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8"
                          asChild
                        >
                          <Link to={integration.href}>
                            Configure
                            <ArrowRight className="h-3 w-3 ml-1" />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {/* Help Section */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Need a different integration?</CardTitle>
          <CardDescription>
            We're always adding new integrations. Let us know what tools you'd like to connect.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline">
            Request Integration
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}